import React, { useEffect, useState } from 'react';
import {
  Box,
  Button as ChakraButton,
  Center,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  Image,
} from '@chakra-ui/react';
import {
  Button,
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
} from '@spotoninc/nexus-react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import {
  MoreVerticalIcon,
  EditIcon,
  DeleteIcon,
  AddIcon,
} from '@spotoninc/nexus-web-assets';
import { fetchWithAuth } from '../../utils/apiHelpers';
import AddOrEditRoleModal from './addOrEditRoleModal';
import ConfirmationDialog from './ConfirmationDialog';

const EMPTY_STATE_ILLUSTRATION =
  'https://cdn.jsdelivr.net/gh/spotoninc/figma-assets@main/empty-roles.svg'; // Replace with your illustration asset

// Function to delete a role
const deleteRole = async roleId => {
  if (!roleId) {
    console.error('No role ID provided for deletion');
    return false;
  }

  try {
    // Prepare data for deletion
    const data = new FormData();
    data.append('roleId', roleId);
    data.append('type', 'delete');
    data.append('screen_list', undefined);

    // Send the request to the API
    const response = await fetchWithAuth('/api/set_screen_role/', {
      method: 'POST',
      body: data,
    });

    const responseData = await response.json();

    if (responseData.code === undefined) {
      return true;
    } else {
      window.open('/login', '_self');
      console.error('Session expired');
      return false;
    }
  } catch (err) {
    console.error('Error deleting role:', err);
    return false;
  }
};

const ManageRoles = () => {
  const [roles, setRoles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDisclosure();
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: TableSortDirection.ascending,
  });
  const toast = useToast();

  // Handle sorting
  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  // Sort roles data
  const sortedRoles = React.useMemo(() => {
    if (!sortConfig.key) {
      return roles;
    }

    return [...roles].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting for created_date
      if (sortConfig.key === 'created_date') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string sorting for description (role name)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
      }
      return 0;
    });
  }, [roles, sortConfig]);

  // Fetch roles from API
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/get_role_data/null/', {
        method: 'GET',
      });
      const data = await res.json();
      if (data.code === undefined) {
        setRoles(data);
      } else {
        window.open('/login', '_self');
        toast({ status: 'error', title: 'Session expired!' });
      }
    } catch (err) {
      toast({ status: 'error', title: 'Failed to fetch roles' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch available screens (permissions)
  const fetchScreens = async () => {
    try {
      const res = await fetchWithAuth('/api/all_screens/', {
        method: 'GET',
      });
      const data = await res.json();
      if (data.code === undefined) {
        setScreens(data.screen_list || []);
      } else {
        window.open('/login', '_self');
        toast({ status: 'error', title: 'Session expired!' });
      }
    } catch (err) {
      toast({ status: 'error', title: 'Failed to fetch permissions' });
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchScreens();
  }, []);

  // Modal handlers
  const handleAddRole = () => {
    setSelectedRole(null);
    setModalMode('add');
    onOpen();
  };
  const handleEditRole = role => {
    setSelectedRole(role);
    setModalMode('edit');
    onOpen();
  };
  const handleDeleteRole = role => {
    if (!role || !role.role_id) {
      toast({ status: 'error', title: 'Invalid role data' });
      return;
    }

    // Set the role to delete and open the confirmation dialog
    setRoleToDelete(role);
    openDeleteDialog();
  };

  // Function to handle the actual deletion after confirmation
  const confirmDeleteRole = async () => {
    if (!roleToDelete || !roleToDelete.role_id) {
      toast({ status: 'error', title: 'Invalid role data' });
      return;
    }

    try {
      // Use the deleteRole function
      const success = await deleteRole(roleToDelete.role_id);

      if (success) {
        toast({ status: 'success', title: 'Role deleted successfully' });
        // Refresh the roles list
        fetchRoles();
      } else {
        toast({ status: 'error', title: 'Failed to delete role' });
      }
    } catch (err) {
      console.error('Error in confirmDeleteRole:', err);
      toast({
        status: 'error',
        title: 'An error occurred while deleting the role',
      });
    }
  };

  // Handle add/edit submit
  const handleRoleSubmit = async formData => {
    onClose();
    fetchRoles();
  };

  // Empty state per Figma
  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }
  if (!roles || roles.length === 0) {
    return (
      <Center minH="60vh" flexDirection="column" gap={6}>
        <Image
          src={EMPTY_STATE_ILLUSTRATION}
          alt="No roles"
          boxSize="180px"
          mb={4}
        />
        <Heading size="lg">No roles defined yet</Heading>
        <Text color="gray.500" maxW="sm" textAlign="center">
          Roles help you manage permissions and access for your team. Get
          started by creating your first role.
        </Text>
        <ChakraButton
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={handleAddRole}
          size="lg"
        >
          Add Role
        </ChakraButton>
        <AddOrEditRoleModal
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleRoleSubmit}
          role={selectedRole}
          screens={screens}
          mode={modalMode}
        />

        {/* Delete confirmation dialog */}
        <ConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={closeDeleteDialog}
          onConfirm={confirmDeleteRole}
          title="Delete Role"
          body="Deleting this role will revoke all its permissions and cannot be undone."
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          confirmButtonColor="#D54041"
        />
      </Center>
    );
  }

  // Populated state per Figma
  return (
    <Box
      ml={{ base: '16px', md: '40px' }}
      mr={{ base: '16px', md: '72px' }}
      py={10}
    >
      <Flex justify="space-between" align="center" mb={8}>
        <Heading
          size="lg"
          fontFamily={'var(--font-family-sans) !important'}
          fontWeight={600}
          color={'var(--semantic-color-text-neutral-strongest-none)'}
        >
          Manage Roles
        </Heading>
        <Button
          colorScheme="blue"
          onClick={handleAddRole}
          leadingIcon={AddIcon}
        >
          Add Role
        </Button>
      </Flex>
      <Box overflow="hidden" bg="white">
        <Table>
          <TableHead>
            <TableRow variant="filled">
              <TableHeaderSortCell
                onSort={direction => handleSort('description', direction)}
                direction={
                  sortConfig.key === 'description'
                    ? sortConfig.direction
                    : undefined
                }
              >
                Role Name
              </TableHeaderSortCell>
              <TableHeaderSortCell
                onSort={direction => handleSort('created_date', direction)}
                direction={
                  sortConfig.key === 'created_date'
                    ? sortConfig.direction
                    : undefined
                }
              >
                Created
              </TableHeaderSortCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRoles.map(role => (
              <TableRow key={role.role_id}>
                <TableCell>{role.description}</TableCell>
                <TableCell>
                  <Flex align="center" justify="space-between">
                    <span>
                      {role.created_date
                        ? new Date(role.created_date).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            }
                          )
                        : ''}
                    </span>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<MoreVerticalIcon />}
                        variant="ghost"
                        borderRadius="full"
                      />
                      <MenuList borderRadius="md" bg="gray.100">
                        <MenuItem
                          height="48px"
                          bg="gray.100"
                          icon={<Icon as={EditIcon} w="24px" h="24px" />}
                          onClick={() => handleEditRole(role)}
                          _hover={{ bg: '#D3E5FF' }}
                          borderRadius="md"
                        >
                          Edit
                        </MenuItem>
                        <Tooltip
                          label="Default roles can't be deleted"
                          isDisabled={!!role.created_user_id_id}
                          hasArrow
                          placement="top"
                          bg="gray.700"
                          color="white"
                        >
                          <MenuItem
                            height="48px"
                            bg="gray.100"
                            icon={<Icon as={DeleteIcon} w="24px" h="24px" />}
                            onClick={() =>
                              role.created_user_id_id && handleDeleteRole(role)
                            }
                            _hover={{
                              bg: role.created_user_id_id
                                ? '#D3E5FF'
                                : 'gray.100',
                            }}
                            borderRadius="md"
                            isDisabled={!role.created_user_id_id}
                            opacity={role.created_user_id_id ? 1 : 0.4}
                            cursor={
                              role.created_user_id_id
                                ? 'pointer'
                                : 'not-allowed'
                            }
                          >
                            Delete
                          </MenuItem>
                        </Tooltip>
                      </MenuList>
                    </Menu>
                  </Flex>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <AddOrEditRoleModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleRoleSubmit}
        role={selectedRole}
        screens={screens}
        mode={modalMode}
      />

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteRole}
        title="Delete Role"
        body="Deleting this role will revoke all its permissions and cannot be
          undone."
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmButtonColor="#D54041"
      />
    </Box>
  );
};

export default ManageRoles;
